/**
 * Drawn map areas in the URL (`?draw=`), so a shared or reloaded link
 * keeps them (owner, 2026-10-12). A ring is a list of [lng, lat]
 * vertices (already simplified by the map when the pen lifts). Each
 * coordinate is rounded to 1e-5 degrees (about a metre) and written as
 * the delta from the previous one: zig-zag signed, then 5-bit chunks
 * with a continuation bit, each chunk one character of a URL-safe
 * alphabet. Rings are joined with ".". A thirty-vertex ring is about
 * 150 characters.
 */

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
const INDEX = new Map(Array.from(ALPHABET, (c, i) => [c, i] as const));
const SCALE = 1e5;
export const MAX_RINGS = 20;
export const MAX_VERTICES = 500;

function pushValue(out: string[], value: number): void {
  let v = value < 0 ? ~(value << 1) : value << 1; // zig-zag
  while (v >= 0x20) {
    out.push(ALPHABET[(v & 0x1f) | 0x20]);
    v >>= 5;
  }
  out.push(ALPHABET[v]);
}

export function encodeRing(ring: [number, number][]): string {
  const out: string[] = [];
  let prevLng = 0;
  let prevLat = 0;
  for (const [lng, lat] of ring) {
    const qLng = Math.round(lng * SCALE);
    const qLat = Math.round(lat * SCALE);
    pushValue(out, qLng - prevLng);
    pushValue(out, qLat - prevLat);
    prevLng = qLng;
    prevLat = qLat;
  }
  return out.join("");
}

export function decodeRing(s: string): [number, number][] | null {
  const ring: [number, number][] = [];
  let i = 0;
  let lng = 0;
  let lat = 0;
  const read = (): number | null => {
    let result = 0;
    let shift = 0;
    for (;;) {
      if (i >= s.length) return null;
      const b = INDEX.get(s[i++]);
      if (b === undefined) return null;
      result |= (b & 0x1f) << shift;
      shift += 5;
      if ((b & 0x20) === 0) break;
      if (shift > 30) return null;
    }
    return result & 1 ? ~(result >> 1) : result >> 1;
  };
  while (i < s.length) {
    const dLng = read();
    const dLat = read();
    if (dLng === null || dLat === null) return null;
    lng += dLng;
    lat += dLat;
    const x = lng / SCALE;
    const y = lat / SCALE;
    if (x < -180 || x > 180 || y < -90 || y > 90) return null;
    ring.push([x, y]);
    if (ring.length > MAX_VERTICES) return null;
  }
  return ring.length >= 3 ? ring : null;
}

/** Every ring as one URL value; empty when there is nothing drawn. */
export function encodeRings(rings: [number, number][][]): string {
  return rings.filter((r) => r.length >= 3).map(encodeRing).join(".");
}

/** The rings of a `?draw=` value; malformed input gives no rings. */
export function decodeRings(value: string | null | undefined): [number, number][][] {
  if (!value) return [];
  const parts = value.split(".").filter(Boolean).slice(0, MAX_RINGS);
  const rings: [number, number][][] = [];
  for (const part of parts) {
    const ring = decodeRing(part);
    if (ring) rings.push(ring);
  }
  return rings;
}

/** The rings as the API's repeatable `polygon` values: flat
 *  "lng,lat,lng,lat,..." strings at 1e-5 degrees (services/draw_area). */
export function ringsToPolygonParams(rings: [number, number][][]): string[] {
  return rings
    .filter((ring) => ring.length >= 3)
    .map((ring) => ring.map(([lng, lat]) => `${lng.toFixed(5)},${lat.toFixed(5)}`).join(","));
}

/** Inverse of ringsToPolygonParams (a saved alert stores the API form);
 *  malformed values are skipped. */
export function polygonParamsToRings(values: string[]): [number, number][][] {
  const rings: [number, number][][] = [];
  for (const raw of values.slice(0, MAX_RINGS)) {
    const nums = raw.split(",").map((x) => Number(x));
    if (nums.length < 6 || nums.some((n) => Number.isNaN(n))) continue;
    const ring: [number, number][] = [];
    for (let i = 0; i + 1 < nums.length; i += 2) ring.push([nums[i], nums[i + 1]]);
    if (ring.length >= 3) rings.push(ring.slice(0, MAX_VERTICES));
  }
  return rings;
}

/** How many areas a `?draw=` value holds (0 when none or malformed). */
export function drawnAreaCount(value: string | null | undefined): number {
  return decodeRings(value).length;
}

/** Tolerance in degrees (about 33 m at Baku's latitude) for simplifyPath:
 *  keeps a freehand stroke's character while turning it into a few
 *  straight edges. */
export const SIMPLIFY_EPS_DEG = 0.0003;

/** Perpendicular distance (planar degrees, fine at city scale) from p to a-b. */
function perpDist(p: [number, number], a: [number, number], b: [number, number]): number {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  let tt = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2;
  tt = Math.max(0, Math.min(1, tt));
  return Math.hypot(p[0] - (a[0] + tt * dx), p[1] - (a[1] + tt * dy));
}

/** Ramer-Douglas-Peucker: the fewest straight segments staying within
 *  `eps` of the freehand path. */
export function simplifyPath(pts: [number, number][], eps: number): [number, number][] {
  if (pts.length <= 2) return pts;
  let maxD = 0, idx = 0;
  const a = pts[0], b = pts[pts.length - 1];
  for (let i = 1; i < pts.length - 1; i++) {
    const d = perpDist(pts[i], a, b);
    if (d > maxD) { maxD = d; idx = i; }
  }
  if (maxD > eps) {
    const left = simplifyPath(pts.slice(0, idx + 1), eps);
    const right = simplifyPath(pts.slice(idx), eps);
    return [...left.slice(0, -1), ...right];
  }
  return [a, b];
}

/** The rings' bounding box as [[minLng, minLat], [maxLng, maxLat]]
 *  (Mapbox's fitBounds order), or null when there is nothing drawn. */
export function ringsBounds(rings: [number, number][][]): [[number, number], [number, number]] | null {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  for (const ring of rings) {
    for (const [lng, lat] of ring) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
  }
  return Number.isFinite(minLng) ? [[minLng, minLat], [maxLng, maxLat]] : null;
}
